import React, { useEffect, useState } from 'react';
import { Box, Container, Link as MuiLink, Stack, Typography } from '@mui/material';
import {
  LocalShipping as ShippingIcon,
  Replay as ReturnsIcon,
  Lock as SecureIcon,
  VerifiedUser as SafeIcon,
  CardGiftcard as GiftIcon,
  SupportAgent as SupportIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import { API_ENDPOINTS, APP_NAME, APP_SLOGAN } from '../../constants';
import { FooterContent } from '../../types';

/** Icon keys the admin can pick from for trust badges */
export const FOOTER_ICONS: Record<string, React.ReactElement> = {
  shipping: <ShippingIcon sx={{ fontSize: 18 }} />,
  returns: <ReturnsIcon sx={{ fontSize: 18 }} />,
  secure: <SecureIcon sx={{ fontSize: 18 }} />,
  safe: <SafeIcon sx={{ fontSize: 18 }} />,
  gift: <GiftIcon sx={{ fontSize: 18 }} />,
  support: <SupportIcon sx={{ fontSize: 18 }} />,
  star: <StarIcon sx={{ fontSize: 18 }} />,
};

// Shown until the CMS content loads (mirrors the backend defaults)
const FALLBACK_FOOTER: FooterContent = {
  brand: {
    description: `${APP_SLOGAN} Affordable Korean-inspired jewellery for everyday styling, gifting and special moments.`,
    trustBadges: [
      { id: 'shipping', icon: 'shipping', text: 'Free Shipping', visible: true },
      { id: 'returns', icon: 'returns', text: '7-Day Returns', visible: true },
      { id: 'secure', icon: 'secure', text: 'Secure Checkout', visible: true },
      { id: 'safe', icon: 'safe', text: 'Skin-Safe', visible: true },
    ],
  },
  columns: [
    {
      id: 'shop',
      heading: 'Shop',
      visible: true,
      order: 1,
      links: [
        { id: 'new', label: 'New arrivals', href: '/products', visible: true, order: 1 },
        { id: 'under-399', label: 'Under ₹399', href: '/products?maxPrice=399', visible: true, order: 2 },
        { id: 'under-599', label: 'Under ₹599', href: '/products?maxPrice=599', visible: true, order: 3 },
      ],
    },
    {
      id: 'help',
      heading: 'Help',
      visible: true,
      order: 2,
      links: [
        { id: 'account', label: 'My account', href: '/account', visible: true, order: 1 },
        { id: 'shipping-policy', label: 'Shipping Policy', href: '/shipping-policy', visible: true, order: 2 },
        { id: 'return-policy', label: 'Return Policy', href: '/return-policy', visible: true, order: 3 },
        { id: 'refund-policy', label: 'Refund & Cancellation', href: '/refund-policy', visible: true, order: 4 },
        { id: 'privacy-policy', label: 'Privacy Policy', href: '/privacy-policy', visible: true, order: 5 },
        { id: 'terms', label: 'Terms & Conditions', href: '/terms-and-conditions', visible: true, order: 6 },
        { id: 'contact', label: 'Contact us', href: '/contact-us', visible: true, order: 7 },
      ],
    },
  ],
  contact: {
    heading: 'Get in touch',
    email: 'support@vediluxe.com',
    phone: '+91 9990145810',
    hours: 'Mon - Sat, 10am - 6pm IST',
    paymentLabel: 'We accept',
    paymentMethods: ['UPI', 'COD', 'Cards'],
  },
  bottom: {
    copyrightSuffix: 'All rights reserved.',
    links: [
      { id: 'privacy', label: 'Privacy Policy', href: '/privacy-policy', visible: true, order: 1 },
      { id: 'terms', label: 'Terms', href: '/terms-and-conditions', visible: true, order: 2 },
    ],
  },
};

const headingSx = {
  mb: 1.25,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  fontSize: '0.75rem',
};

const Footer: React.FC = () => {
  const [content, setContent] = useState<FooterContent>(FALLBACK_FOOTER);

  useEffect(() => {
    const load = () => {
      apiClient
        .get(`${API_ENDPOINTS.SITE_SETTINGS}/footer`)
        .then((response) => {
          if (response.data?.footer?.columns) {
            setContent(response.data.footer as FooterContent);
          }
        })
        .catch(() => {});
    };
    load();
    window.addEventListener('footer-updated', load);
    return () => window.removeEventListener('footer-updated', load);
  }, []);

  const visibleColumns = content.columns
    .filter((col) => col.visible)
    .sort((a, b) => a.order - b.order);
  const visibleBadges = content.brand.trustBadges.filter((b) => b.visible);
  const bottomLinks = (content.bottom.links || [])
    .filter((l) => l.visible)
    .sort((a, b) => a.order - b.order);

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        pt: { xs: 5, md: 6 },
        pb: 3,
        background:
          'linear-gradient(180deg, rgba(252, 247, 250, 0.95) 0%, rgba(246, 241, 247, 0.98) 100%)',
        borderTop: '1px solid rgba(43, 37, 43, 0.08)'
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: '1.4fr 0.8fr 0.8fr 1fr' },
            gap: { xs: 3, md: 4 },
            mb: 4
          }}
        >
          {/* Brand column */}
          <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
            <Typography variant="h5" sx={{ mb: 1, fontFamily: 'Playfair Display' }}>
              {APP_NAME}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 340 }}>
              {content.brand.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 1.5 }}>
              {visibleBadges.map((badge) => (
                <Box
                  key={badge.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}
                >
                  {FOOTER_ICONS[badge.icon] || FOOTER_ICONS.star}
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {badge.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Link columns (admin-managed) */}
          {visibleColumns.map((column) => (
            <Box key={column.id}>
              <Typography variant="subtitle2" sx={headingSx}>
                {column.heading}
              </Typography>
              <Stack spacing={0.75}>
                {(column.links || [])
                  .filter((link) => link.visible)
                  .sort((a, b) => a.order - b.order)
                  .map((link) => (
                    <MuiLink
                      key={link.id}
                      component={RouterLink}
                      to={link.href}
                      underline="none"
                      color="text.secondary"
                      variant="body2"
                      sx={{ '&:hover': { color: 'primary.main' } }}
                    >
                      {link.label}
                    </MuiLink>
                  ))}
              </Stack>
            </Box>
          ))}

          {/* Get in touch */}
          <Box>
            <Typography variant="subtitle2" sx={headingSx}>
              {content.contact.heading}
            </Typography>
            <Stack spacing={1}>
              {content.contact.email && (
                <MuiLink
                  href={`mailto:${content.contact.email}`}
                  underline="none"
                  color="text.secondary"
                  variant="body2"
                  sx={{ '&:hover': { color: 'primary.main' } }}
                >
                  {content.contact.email}
                </MuiLink>
              )}
              {content.contact.phone && (
                <MuiLink
                  href={`tel:${content.contact.phone.replace(/\s+/g, '')}`}
                  underline="none"
                  color="text.secondary"
                  variant="body2"
                  sx={{ '&:hover': { color: 'primary.main' } }}
                >
                  {content.contact.phone}
                </MuiLink>
              )}
              {content.contact.hours && (
                <Typography variant="body2" color="text.secondary">
                  {content.contact.hours}
                </Typography>
              )}
              {(content.contact.paymentMethods || []).length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    {content.contact.paymentLabel}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {content.contact.paymentMethods.map((method) => (
                      <Box
                        key={method}
                        sx={{
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 1,
                          border: '1px solid rgba(0,0,0,0.1)',
                          bgcolor: '#fff',
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.68rem' }}>
                          {method}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Stack>
          </Box>
        </Box>

        {/* Bottom bar */}
        <Box
          sx={{
            pt: 2.5,
            borderTop: '1px solid rgba(43, 37, 43, 0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1.5,
            flexDirection: { xs: 'column', sm: 'row' }
          }}
        >
          <Typography variant="caption" color="text.secondary">
            &copy; {new Date().getFullYear()} {APP_NAME}. {content.bottom.copyrightSuffix}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {bottomLinks.map((link) => (
              <MuiLink
                key={link.id}
                component={RouterLink}
                to={link.href}
                underline="none"
                variant="caption"
                color="text.secondary"
                sx={{ '&:hover': { color: 'primary.main' } }}
              >
                {link.label}
              </MuiLink>
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
